import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { CONTACT_FIELDS, CONTACT_PRESETS, isLiveEntry, type ConditionEntry } from '@shared/segments'
import { toCsvRow } from '@shared/csv'
import { api, ApiError } from '../lib/api'
import { SegmentBuilder } from './SegmentBuilder'

/**
 * Everyone the workspace has ever messaged or been messaged by, with a live filter over the
 * same fields a segment can name — so building an audience here and reusing it as a broadcast
 * recipient list is the same segment, not two things that can drift apart.
 */
export function Contacts() {
  const queryClient = useQueryClient()
  const [q, setQ] = useState('')
  const [segmentId, setSegmentId] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [building, setBuilding] = useState(false)
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null)
  const [match, setMatch] = useState<'all' | 'any'>('all')
  const [conditions, setConditions] = useState<ConditionEntry[]>([])
  const [segmentName, setSegmentName] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [companyInput, setCompanyInput] = useState('')
  const [groupByCompany, setGroupByCompany] = useState(false)

  const contacts = useQuery({
    queryKey: ['contacts', q, segmentId],
    queryFn: () => api.contacts({ q: q || undefined, segmentId: segmentId || undefined }),
    retry: false,
  })

  const segmentsQuery = useQuery({ queryKey: ['segments'], queryFn: api.segments, retry: false })
  const contactSegments = (segmentsQuery.data?.segments ?? []).filter((s) => s.entity === 'contact')

  // A "Labels" condition typed as free text is a silent-mismatch trap — "delivery" against the
  // real "Delivery" label just never matches, with nothing on screen to say why. Filling in the
  // field's own `options` turns it into the same picker every fixed-choice field already gets.
  const team = useQuery({ queryKey: ['team'], queryFn: api.team })
  const labelNames = team.data?.labels.map((l) => l.name)
  const contactFields = CONTACT_FIELDS.map((f) =>
    f.key === 'labels' && labelNames?.length ? { ...f, options: labelNames } : f,
  )

  const preview = useQuery({
    queryKey: ['segment-preview', 'contact', match, conditions],
    queryFn: () => api.previewSegment({ entity: 'contact', match, conditions }),
    enabled: building && conditions.some(isLiveEntry),
    retry: false,
  })

  const refreshContacts = () => queryClient.invalidateQueries({ queryKey: ['contacts'] })
  const refreshSegments = () => queryClient.invalidateQueries({ queryKey: ['segments'] })

  // One mutation for both: editing a segment is the identical builder pre-filled from the
  // segment being changed rather than a second screen, so saving it is the same "am I updating
  // an id or minting one" branch the task/ticket editors elsewhere already use.
  const saveSegment = useMutation({
    mutationFn: async (): Promise<{ id: string }> =>
      editingSegmentId
        ? await api.updateSegment(editingSegmentId, { name: segmentName, match, conditions }).then(() => ({ id: editingSegmentId }))
        : await api.createSegment({ name: segmentName, entity: 'contact', match, conditions }),
    onSuccess: (result) => {
      setBuilding(false)
      setEditingSegmentId(null)
      setSegmentName('')
      setConditions([])
      void refreshSegments()
      setSegmentId(result.id)
    },
  })

  const deleteSegment = useMutation({
    mutationFn: (id: string) => api.deleteSegment(id),
    onSuccess: () => {
      if (contactSegments.some((s) => s.id === segmentId)) setSegmentId('')
      void refreshSegments()
    },
  })

  const tag = useMutation({
    mutationFn: (input: { tag: string; mode: 'add' | 'remove' }) =>
      api.tagContacts({ contactIds: [...selected], tag: input.tag, mode: input.mode }),
    onSuccess: () => {
      setSelected(new Set())
      setTagInput('')
      void refreshContacts()
    },
  })

  const optOut = useMutation({
    mutationFn: (optedOut: boolean) => api.setContactsOptedOut({ contactIds: [...selected], optedOut }),
    onSuccess: () => {
      setSelected(new Set())
      void refreshContacts()
    },
  })

  const setCompany = useMutation({
    mutationFn: (company: string) => api.setContactsCompany({ contactIds: [...selected], company: company || null }),
    onSuccess: () => {
      setSelected(new Set())
      setCompanyInput('')
      void refreshContacts()
    },
  })

  if (contacts.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (contacts.isError) {
    const message = contacts.error instanceof ApiError ? contacts.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { contacts: rows, total, appliedSegment, canManage, scoped } = contacts.data!

  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.id)))
  const toggleOne = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Not a query param — the same rows the table already has, just re-shown with a company
  // heading between groups instead of a filter that would drop everyone with no company set.
  const companyGroups: { company: string; rows: typeof rows }[] = groupByCompany
    ? Object.entries(
        rows.reduce<Record<string, typeof rows>>((acc, row) => {
          const key = row.company?.trim() || 'No company'
          ;(acc[key] ??= []).push(row)
          return acc
        }, {}),
      )
        .sort(([a], [b]) => (a === 'No company' ? 1 : b === 'No company' ? -1 : a.localeCompare(b)))
        .map(([company, groupRows]) => ({ company, rows: groupRows }))
    : [{ company: '', rows }]

  const exportSelected = () => {
    const chosen = rows.filter((row) => selected.has(row.id))
    const header = ['Name', 'Phone', 'Company', 'Tags', 'Labels', 'Last messaged', 'Open tickets']
    const lines = [header, ...chosen.map((row) => [
      row.name ?? '',
      row.phone,
      row.company ?? '',
      row.tags.join(' '),
      row.labels.join(' '),
      row.lastMessageAt ?? '',
      row.openTickets,
    ])]
    const csv = `﻿${lines.map(toCsvRow).join('\r\n')}\r\n`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'contacts.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">Contacts</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">{total.toLocaleString()} {total === 1 ? 'contact' : 'contacts'}</h1>
          <div className="row gap-2 wrap">
            <input
              className="cad-input"
              style={{ width: '220px' }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or phone"
            />
            <button
              type="button"
              className="cad-btn"
              data-variant={groupByCompany ? undefined : 'quiet'}
              onClick={() => setGroupByCompany((v) => !v)}
            >
              Group by company
            </button>
            {!building && (
              <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setBuilding(true)}>
                Build a filter
              </button>
            )}
          </div>
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          A filter saved here becomes a segment — the same audience you can pick when composing a
          bulk send, resolved fresh against current contacts every time it is used.
        </p>
        {scoped && (
          <p className="cad-note">
            You only see contacts with a conversation under a label you have been granted.
          </p>
        )}
      </header>

      {contactSegments.length > 0 && (
        <div className="row gap-2 wrap">
          <button
            type="button"
            className="cad-pill"
            data-tone={segmentId === '' ? 'accent' : undefined}
            onClick={() => setSegmentId('')}
          >
            All contacts
          </button>
          {contactSegments.map((segment) => (
            <button
              key={segment.id}
              type="button"
              className="cad-pill"
              data-tone={segmentId === segment.id ? 'accent' : undefined}
              onClick={() => setSegmentId(segment.id)}
              title={segment.description}
            >
              {segment.name}
            </button>
          ))}
          {appliedSegment && canManage && (
            <button
              type="button"
              className="cad-btn"
              data-variant="text"
              onClick={() => {
                const segment = contactSegments.find((s) => s.id === appliedSegment.id)
                if (!segment) return
                setEditingSegmentId(segment.id)
                setSegmentName(segment.name)
                setMatch(segment.match)
                setConditions(segment.conditions)
                setBuilding(true)
              }}
            >
              Edit “{appliedSegment.name}”
            </button>
          )}
          {appliedSegment && canManage && (
            <button
              type="button"
              className="cad-btn"
              data-variant="text"
              disabled={deleteSegment.isPending}
              onClick={() => {
                if (confirm(`Delete the "${appliedSegment.name}" segment?

The contacts stay; the saved filter does not. Anything scheduled against this segment loses its audience.`)) deleteSegment.mutate(appliedSegment.id)
              }}
            >
              Delete “{appliedSegment.name}”
            </button>
          )}
        </div>
      )}

      {deleteSegment.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {deleteSegment.error instanceof ApiError || deleteSegment.error instanceof Error
            ? deleteSegment.error.message
            : 'Could not delete that segment.'}
        </p>
      )}

      {building && (
        <section className="cad-card">
          <div className="stack gap-4">
            <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
              <h2 className="cad-section-title">Build a filter</h2>
              <div className="row gap-2 wrap">
                {CONTACT_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    className="cad-pill"
                    onClick={() => {
                      setMatch(preset.segment.match)
                      setConditions(preset.segment.conditions)
                    }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {conditions.length === 0 ? (
              <p className="cad-muted">No conditions yet — add one, or start from a preset above.</p>
            ) : (
              <SegmentBuilder
                fields={contactFields}
                match={match}
                conditions={conditions}
                onChange={(next) => {
                  setMatch(next.match)
                  setConditions(next.conditions)
                }}
              />
            )}
            {conditions.length === 0 && (
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                onClick={() => setConditions([{ field: CONTACT_FIELDS[0].key, operator: 'is', value: '' }])}
              >
                Add condition
              </button>
            )}

            {preview.data && (
              <p className="cad-note">
                {preview.data.description} · matches {preview.data.count.toLocaleString()} contact
                {preview.data.count === 1 ? '' : 's'} right now.
              </p>
            )}
            {saveSegment.error && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                {saveSegment.error instanceof ApiError || saveSegment.error instanceof Error
                  ? saveSegment.error.message
                  : 'Could not save that segment.'}
              </p>
            )}

            <div className="field-grid">
              <div className="cad-field">
                <label htmlFor="seg-name">Name this segment</label>
                <input
                  id="seg-name"
                  className="cad-input"
                  value={segmentName}
                  onChange={(e) => setSegmentName(e.target.value)}
                  placeholder="Gone quiet — India"
                />
              </div>
            </div>

            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                disabled={
                  !segmentName.trim() ||
                  !conditions.some(isLiveEntry) ||
                  (preview.data?.problems.length ?? 0) > 0 ||
                  saveSegment.isPending
                }
                onClick={() => saveSegment.mutate()}
              >
                {saveSegment.isPending ? 'Saving…' : editingSegmentId ? 'Save changes' : 'Save segment'}
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                onClick={() => {
                  setBuilding(false)
                  setEditingSegmentId(null)
                  setConditions([])
                  setSegmentName('')
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </section>
      )}

      {selected.size > 0 && (
        <div className="cad-card" style={{ position: 'sticky', top: '8px', zIndex: 1 }}>
          <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
            <span>{selected.size} selected</span>
            <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              <input
                className="cad-input"
                style={{ width: '140px' }}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Tag name"
              />
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={!tagInput.trim() || tag.isPending}
                onClick={() => tag.mutate({ tag: tagInput.trim(), mode: 'add' })}
              >
                Add tag
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={!tagInput.trim() || tag.isPending}
                onClick={() => tag.mutate({ tag: tagInput.trim(), mode: 'remove' })}
              >
                Remove tag
              </button>
              <input
                className="cad-input"
                style={{ width: '140px' }}
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                placeholder="Company"
              />
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={setCompany.isPending}
                onClick={() => setCompany.mutate(companyInput.trim())}
                title={companyInput.trim() ? undefined : 'Leave blank to clear the company on selected contacts'}
              >
                Set company
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={optOut.isPending}
                onClick={() => optOut.mutate(true)}
              >
                Opt out
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={optOut.isPending}
                onClick={() => optOut.mutate(false)}
              >
                Opt back in
              </button>
              <button type="button" className="cad-btn" data-variant="quiet" onClick={exportSelected}>
                Export CSV
              </button>
              <button type="button" className="cad-btn" data-variant="text" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </div>
          </div>
          {(tag.error || optOut.error || setCompany.error) && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)', marginTop: '10px' }}>
              {(() => {
                const err = tag.error ?? optOut.error ?? setCompany.error
                return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
              })()}
            </p>
          )}
        </div>
      )}

      <section className="cad-card">
        {rows.length === 0 ? (
          <p className="cad-muted">No contacts match.</p>
        ) : (
          <div className="cad-grid-frame">
            <table className="cad-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  </th>
                  <th>Contact</th>
                  <th>Company</th>
                  <th>Tags</th>
                  <th>Labels</th>
                  <th>Last messaged</th>
                  <th className="num">Open tickets</th>
                </tr>
              </thead>
              <tbody>
                {companyGroups.map((group) => (
                  <Fragment key={group.company || 'flat'}>
                    {groupByCompany && (
                      <tr key={`group-${group.company}`}>
                        <td colSpan={7} className="cad-muted" style={{ fontWeight: 600, background: 'var(--cad-surface-sunken)' }}>
                          {group.company} · {group.rows.length}
                        </td>
                      </tr>
                    )}
                    {group.rows.map((row) => (
                      <tr key={row.id} data-selected={selected.has(row.id)}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(row.id)}
                            onChange={() => toggleOne(row.id)}
                          />
                        </td>
                        <td>
                          <div className="stack gap-1">
                            <span style={{ fontWeight: 500 }}>{row.name || row.phone}</span>
                            <span className="cad-muted mono" style={{ fontSize: 'var(--cad-text-base)' }}>
                              {row.phone}
                              {row.optedOut && (
                                <span className="cad-pill" data-tone="bad" style={{ marginLeft: '6px' }}>
                                  opted out
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="cad-muted">{row.company || '—'}</td>
                        <td>
                          <div className="row gap-1 wrap">
                            {row.tags.map((t) => (
                              <span key={t} className="cad-pill">
                                {t}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="row gap-1 wrap">
                            {row.labels.map((label) => (
                              <span key={label} className="cad-pill" data-tone="accent">
                                {label}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="cad-muted">
                          {row.lastMessageAt ? new Date(row.lastMessageAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="num">{row.openTickets}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
